/**
 * Audit logging constants, types, and services.
 * 
 * @module lib/audit
 */

import { supabase } from './supabase';
import type { AdminAuditLogInsert } from '../types/database';

/**
 * Action types for audit logging.
 * Used to categorize admin actions for compliance tracking.
 */
export const AuditActionTypes = {
  // Authentication
  LOGIN: 'login',
  LOGOUT: 'logout',
  LOGIN_FAILED: 'login_failed',

  // Dashboard
  VIEW_DASHBOARD: 'view_dashboard',

  // Users
  VIEW_USERS_LIST: 'view_users_list',
  VIEW_USER: 'view_user',
  SEARCH_USERS: 'search_users',

  // Businesses
  VIEW_BUSINESSES_LIST: 'view_businesses_list',
  VIEW_BUSINESS: 'view_business',
  SEARCH_BUSINESSES: 'search_businesses',

  // Reports
  VIEW_REPORTS_LIST: 'view_reports_list',
  VIEW_REPORT: 'view_report',
  VIEW_RESPONSES: 'view_responses',

  // Content
  VIEW_CONTENT_CHECKER_LIST: 'view_content_checker_list',
  VIEW_CONTENT_CHECKER: 'view_content_checker',
  VIEW_FAQ_AUDIT_LIST: 'view_faq_audit_list',
  VIEW_FAQ_AUDIT: 'view_faq_audit',
  VIEW_LLMS_TXT_LIST: 'view_llms_txt_list',
  VIEW_LLMS_TXT: 'view_llms_txt',

  // Audit
  VIEW_AUDIT_LOGS: 'view_audit_logs',

  // Other
  EXPORT_DATA: 'export_data',
} as const;

/**
 * Type for audit action values.
 */
export type AuditActionType = (typeof AuditActionTypes)[keyof typeof AuditActionTypes];

/**
 * Resource types for audit logging.
 */
export const AuditResourceTypes = {
  USER: 'user',
  BUSINESS: 'business',
  REPORT: 'report',
  RESPONSE: 'response',
  CONTENT_CHECKER: 'content_checker',
  FAQ_AUDIT: 'faq_audit',
  LLMS_TXT: 'llms_txt',
  AUDIT_LOG: 'audit_log',
} as const;

/**
 * Type for resource type values.
 */
export type AuditResourceType = (typeof AuditResourceTypes)[keyof typeof AuditResourceTypes];

/**
 * Cached IP address to avoid repeated API calls.
 */
let cachedIpAddress: string | null = null;

/**
 * Fetches the client's public IP address.
 * Uses ipify.org API and caches the result for the session.
 * 
 * @returns The client's public IP address or null if unavailable
 * 
 * @security IP address is collected for audit logging and security purposes.
 * @see CSDEV-140 - IP capture strategy
 */
export async function getClientIpAddress(): Promise<string | null> {
  // Return cached value if available
  if (cachedIpAddress) {
    return cachedIpAddress;
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    const response = await fetch('https://api.ipify.org?format=json', {
      method: 'GET',
      signal: controller.signal, // 5 second timeout
    });
    clearTimeout(timeoutId);

    if (!response.ok) {
      console.warn('Failed to fetch IP address:', response.status);
      return null;
    }

    const data = await response.json();
    cachedIpAddress = data.ip || null;
    return cachedIpAddress;
  } catch (error) {
    console.warn('Error fetching IP address:', error);
    return null;
  }
}

/**
 * Gets the current user agent string.
 * 
 * @returns The browser's user agent string
 */
export function getUserAgent(): string {
  return typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown';
}

/**
 * Parameters for creating an audit log entry.
 */
export interface AuditLogParams {
  /** Admin user ID (UUID) */
  adminUserId: string;
  /** Admin user email */
  adminEmail: string;
  /** Type of action performed */
  actionType: AuditActionType;
  /** Type of resource accessed (optional) */
  resourceType?: AuditResourceType;
  /** ID of the resource accessed (optional) */
  resourceId?: string;
  /** Additional details about the resource (optional) */
  resourceDetails?: Record<string, unknown>;
}

/**
 * Creates an audit log entry in the database.
 * Automatically captures IP address and user agent.
 * 
 * SECURITY: This function logs all admin actions for compliance tracking.
 * Failed inserts are logged to console but do not throw to avoid disrupting user flows.
 * 
 * @param params - Audit log parameters
 * @returns Promise that resolves when the log is created
 * 
 * @example
 * ```typescript
 * await createAuditLog({
 *   adminUserId: 'uuid-here',
 *   adminEmail: 'admin@example.com',
 *   actionType: AuditActionTypes.LOGIN,
 * });
 * ```
 * 
 * @security Audit logs are critical for security compliance.
 * @see CSDEV-139 - Audit logging for login/logout/failed login
 * @see CSDEV-140 - IP capture strategy
 */
export async function createAuditLog(params: AuditLogParams): Promise<void> {
  try {
    // Fetch IP in parallel with preparing other data
    const [ipAddress] = await Promise.all([getClientIpAddress()]);
    const userAgent = getUserAgent();

    const insertData: AdminAuditLogInsert = {
      admin_user_id: params.adminUserId,
      admin_email: params.adminEmail,
      action_type: params.actionType,
      resource_type: params.resourceType || null,
      resource_id: params.resourceId || null,
      resource_details: (params.resourceDetails as AdminAuditLogInsert['resource_details']) || null,
      ip_address: ipAddress,
      user_agent: userAgent,
    };

    // NOTE: Supabase table type inference returns `never` for admin_audit_logs inserts.
    // This narrow client type preserves compile-time safety without blocking inserts.
    const auditClient = supabase as unknown as {
      from: (table: 'admin_audit_logs') => {
        insert: (
          values: AdminAuditLogInsert
        ) => Promise<{ error: { message: string } | null }>;
      };
    };

    const { error } = await auditClient.from('admin_audit_logs').insert(insertData);

    if (error) {
      console.error('Failed to create audit log:', error);
    }
  } catch (error) {
    // Don't throw - audit logging failures should not disrupt user flows
    console.error('Error creating audit log:', error);
  }
}

/**
 * Creates an audit log for authentication events.
 * Used for login, logout, and failed login attempts.
 * 
 * @param adminUserId - Admin user ID (or null for failed logins)
 * @param adminEmail - Admin email address
 * @param actionType - The authentication action type
 * @param details - Optional additional details
 * 
 * @security Authentication events are critical security logs.
 * @see CSDEV-139 - Audit logging for login/logout/failed login
 */
export async function logAuthEvent(
  adminUserId: string | null,
  adminEmail: string,
  actionType: typeof AuditActionTypes.LOGIN | typeof AuditActionTypes.LOGOUT | typeof AuditActionTypes.LOGIN_FAILED,
  details?: Record<string, unknown>
): Promise<void> {
  // For failed logins, we still want to log but may not have an admin user ID
  // In this case, we can't use RLS insert, so we log to console as fallback
  if (!adminUserId) {
    const ipAddress = await getClientIpAddress();
    console.warn('Auth event (no admin ID):', {
      email: adminEmail,
      action: actionType,
      ip: ipAddress,
      userAgent: getUserAgent(),
      details,
      timestamp: new Date().toISOString(),
    });
    return;
  }

  await createAuditLog({
    adminUserId,
    adminEmail,
    actionType,
    resourceDetails: details,
  });
}
